import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { PaginatedResult } from "../../sessions/contracts/PaginatedResult";
import { CreateProgrammeRequest } from "../contracts/CreateProgrammeRequest";
import { CurriculumTemplateResponse } from "../contracts/CurriculumTemplateResponse";
import { EnrolmentResponse } from "../contracts/EnrolmentResponse";
import { EnrolRequest } from "../contracts/EnrolRequest";
import { ProgrammeResponse } from "../contracts/ProgrammeResponse";
import { ProgrammeSummaryResponse } from "../contracts/ProgrammeSummaryResponse";
import { SaveTopicRequest } from "../contracts/SaveTopicRequest";
import { UpdateEnrolmentRequest } from "../contracts/UpdateEnrolmentRequest";
import { UpdateProgrammeRequest } from "../contracts/UpdateProgrammeRequest";
import {
  mapResponseToCurriculumTemplate,
  mapResponseToEnrolment,
  mapResponseToEnrolResult,
  mapResponseToProgramme,
  mapResponseToProgrammeSummary,
} from "../mappers/TrainingResponseMapper";
import { Enrolment } from "../models/Enrolment";
import { EnrolResult } from "../models/EnrolResult";
import { CurriculumTemplate, Programme, ProgrammeSummary } from "../models/Programme";

const BASE = "/training";
type Rejection = { rejectValue: ApiErrorResponse };

export const fetchCurriculumTemplates = createAsyncThunk<CurriculumTemplate[], void, Rejection>(
  "training/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<CurriculumTemplateResponse[]>(`${BASE}/curriculum-templates`);
      return response.data.map(mapResponseToCurriculumTemplate);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to load templates"));
    }
  }
);

/** The settings list shows every programme; organizations hold a handful, so one page of 100 suffices. */
export const fetchProgrammes = createAsyncThunk<ProgrammeSummary[], void, Rejection>(
  "training/fetchProgrammes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<PaginatedResult<ProgrammeSummaryResponse>>(`${BASE}/programmes`, {
        params: { page: 1, pageSize: 100 },
      });
      return response.data.items.map(mapResponseToProgrammeSummary);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to load programmes"));
    }
  }
);

export const fetchProgramme = createAsyncThunk<Programme, string, Rejection>(
  "training/fetchProgramme",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<ProgrammeResponse>(`${BASE}/programmes/${id}`);
      return mapResponseToProgramme(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to load the programme"));
    }
  }
);

export const createProgramme = createAsyncThunk<Programme, CreateProgrammeRequest, Rejection>(
  "training/createProgramme",
  async (request, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ProgrammeResponse>(`${BASE}/programmes`, request);
      return mapResponseToProgramme(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to create the programme"));
    }
  }
);

export const updateProgramme = createAsyncThunk<
  Programme,
  { id: string; request: UpdateProgrammeRequest },
  Rejection
>("training/updateProgramme", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ProgrammeResponse>(`${BASE}/programmes/${id}`, request);
    return mapResponseToProgramme(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the programme"));
  }
});

export const addTopic = createAsyncThunk<Programme, { programmeId: string; request: SaveTopicRequest }, Rejection>(
  "training/addTopic",
  async ({ programmeId, request }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<ProgrammeResponse>(
        `${BASE}/programmes/${programmeId}/topics`,
        request
      );
      return mapResponseToProgramme(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to add the topic"));
    }
  }
);

export const updateTopic = createAsyncThunk<
  Programme,
  { programmeId: string; topicId: string; request: SaveTopicRequest },
  Rejection
>("training/updateTopic", async ({ programmeId, topicId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ProgrammeResponse>(
      `${BASE}/programmes/${programmeId}/topics/${topicId}`,
      request
    );
    return mapResponseToProgramme(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the topic"));
  }
});

export const archiveTopic = createAsyncThunk<Programme, { programmeId: string; topicId: string }, Rejection>(
  "training/archiveTopic",
  async ({ programmeId, topicId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete<ProgrammeResponse>(
        `${BASE}/programmes/${programmeId}/topics/${topicId}`
      );
      return mapResponseToProgramme(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to archive the topic"));
    }
  }
);

export const fetchMemberEnrolments = createAsyncThunk<
  { memberId: string; enrolments: Enrolment[] },
  string,
  Rejection
>("training/fetchMemberEnrolments", async (memberId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<EnrolmentResponse[]>(`${BASE}/members/${memberId}/enrolments`);
    return { memberId, enrolments: response.data.map(mapResponseToEnrolment) };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load enrolments"));
  }
});

export const enrolMember = createAsyncThunk<EnrolResult, { memberId: string; request: EnrolRequest }, Rejection>(
  "training/enrolMember",
  async ({ memberId, request }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<EnrolmentResponse>(
        `${BASE}/members/${memberId}/enrolments`,
        request
      );
      return mapResponseToEnrolResult(response.data);
    } catch (error) {
      return rejectWithValue(toApiError(error, "Failed to enrol"));
    }
  }
);

export const updateEnrolment = createAsyncThunk<
  Enrolment,
  { memberId: string; enrolmentId: string; request: UpdateEnrolmentRequest },
  Rejection
>("training/updateEnrolment", async ({ memberId, enrolmentId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<EnrolmentResponse>(
      `${BASE}/members/${memberId}/enrolments/${enrolmentId}`,
      request
    );
    return mapResponseToEnrolment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the enrolment"));
  }
});

export const removeEnrolment = createAsyncThunk<
  { memberId: string; enrolmentId: string },
  { memberId: string; enrolmentId: string },
  Rejection
>("training/removeEnrolment", async (args, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${BASE}/members/${args.memberId}/enrolments/${args.enrolmentId}`);
    return args;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to remove the enrolment"));
  }
});
